import { useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router'
import { fetchWorkspaces, signOut, type Me } from '../lib/api'
import { HeaderSearch } from './HeaderSearch'

export interface TopBarProps {
  /** The workspace this session belongs to, by name. Null until the session has answered. */
  workspace: string | null
  loading: boolean
  person: Me | null
  onToggleNav: () => void
  navOpen: boolean
  /** Leaves the app with a full navigation. Injected so a test can observe it. */
  leaveTo?: (url: string) => void
}

function fullNavigation(url: string) {
  window.location.href = url
}

/** The sticky 64px app bar. */
export function TopBar({
  workspace, loading, person, onToggleNav, navOpen, leaveTo = fullNavigation,
}: TopBarProps) {
  const [open, setOpen] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const menu = useRef<HTMLDivElement>(null)

  // How many OTHER workspaces this person could open. Asked the first time the menu opens, not on
  // every page: most people have one, and the answer only decides whether "Switch workspace" shows.
  const [others, setOthers] = useState<number | null>(null)
  useEffect(() => {
    if (!open || others !== null) return
    let live = true
    void fetchWorkspaces().then((result) => {
      if (!live) return
      setOthers(
        result.ok
          ? result.data.workspaces.filter((w) => w.workspaceId !== result.data.current).length
          : 0,
      )
    })
    return () => {
      live = false
    }
  }, [open, others])

  // Prototype screens are mounted only in a development build (App.tsx), so linking to them from a
  // production header sent a signed-in customer to the sign-in page. Read per render, not at module
  // load, so a test can stub the environment.
  const showDevLinks = import.meta.env.DEV

  // Close on a click elsewhere, on Escape, or when keyboard focus leaves it. All three, because a
  // menu that traps a person until they find the same button again is worse than no menu -- and
  // this one is the way OUT of a session somebody may have opened by mistake on a shared machine.
  // Escape hands focus back to the button that opened it; closing under the keyboard used to drop
  // focus onto the page body.
  const trigger = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  useEffect(() => {
    if (!open) return

    function away(event: MouseEvent) {
      if (menu.current && !menu.current.contains(event.target as Node)) setOpen(false)
    }

    function escape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      trigger.current?.focus()
    }

    function focusLeft(event: FocusEvent) {
      if (menu.current && event.target instanceof Node && !menu.current.contains(event.target)) setOpen(false)
    }

    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', escape)
    document.addEventListener('focusin', focusLeft)

    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', escape)
      document.removeEventListener('focusin', focusLeft)
    }
  }, [open])

  // Set when the sign-out request did not come back as done. Shown in the menu, which stays open.
  const [leaveFailed, setLeaveFailed] = useState(false)

  /**
   * Ends the session, and reports whether it did. A failed POST (offline, or a server error while
   * revoking) leaves the HttpOnly cookie in the browser and the session live. Landing on the sign-in
   * page anyway looked exactly like a completed sign-out, and on a shared machine the next person
   * to open the site was routed into this account. So: stay here and say so.
   */
  async function endSession(): Promise<boolean> {
    setLeaving(true)
    setLeaveFailed(false)
    if (await signOut()) return true
    setLeaving(false)
    setLeaveFailed(true)
    return false
  }

  async function leave() {
    if (!(await endSession())) return

    // A full navigation, not a route change. Every piece of state this app holds -- the workspace
    // name in the shell, cached findings, an open menu -- belongs to the session that just ended,
    // and carrying any of it into the next screen is how one account's data appears under
    // another's name. Reloading throws all of it away, which is the point.
    // The flag is why the next sign-in demands credentials instead of being answered from
    // Microsoft's cookie. Not cosmetic: without it, signing out and back in on a shared machine
    // asks the second person nothing at all.
    leaveTo('/auth/sign-in?signed_out=1')
  }

  /**
   * Switching workspace IS signing out and back in. A session never moves between workspaces
   * (migration 0064: re-pointing it would change the meaning of every capability already minted
   * from it), so this ends this one and lets the next sign-in show the chooser -- a person with
   * several workspaces is never auto-placed (0114). No forced re-authentication: it is the same
   * person choosing, not somebody new at the keyboard.
   */
  async function switchWorkspace() {
    if (!(await endSession())) return
    leaveTo('/auth/sign-in?switch=1')
  }

  return (
    <header className="app-bar">
      <button
        type="button"
        className="icon-button"
        aria-label={navOpen ? 'Hide navigation' : 'Show navigation'}
        aria-expanded={navOpen}
        onClick={onToggleNav}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* The wordmark is a link home, which is what people try first. */}
      <Link to="/dashboard" className="wordmark">
        <span className="wordmark-brand">Defenssive</span>
        <span className="wordmark-product">AI Security Manager</span>
      </Link>

      <div className="workspace-label" title={workspace ?? undefined}>
        {loading ? <span className="workspace-label-idle">Loading…</span> : (workspace ?? (
          <span className="workspace-label-idle">No workspace</span>
        ))}
      </div>

      {/*
        Searches findings by name, suggesting as it is typed (HeaderSearch). It was once a box
        labelled "Search findings, devices, users" wired to nothing, then one that opened a finding
        by its reference and refused everything else.
      */}
      <HeaderSearch workspaceId={person?.workspaceId ?? null} />

      {showDevLinks ? (
        <nav className="app-bar-links" aria-label="Development">
          <Link to="/prototype">Prototype</Link>
          <Link to="/primitives">Primitives</Link>
        </nav>
      ) : null}

      {/* Initials rather than a photograph: there is no avatar upload, and a generated face
          would be inventing a person. Derived from the signed-in account rather than hardcoded --
          the placeholder said "JD" for everybody, which is a different person's initials for
          almost every real user. */}
      {/*
        THE ONLY WAY OUT. This button existed and did nothing -- no onClick, no menu -- so there
        was no way to sign out of this product at all. POST /api/auth/sign-out has been there the
        whole time, revoking the entire token family; nothing in the interface called it.
        A person signing in on a shared machine, or with the wrong one of two accounts, was stuck
        until the cookie expired.
      */}
      <div className="account" ref={menu}>
      {/* A disclosure, not an ARIA menu. role="menu" promised arrow-key navigation this never
          had, so a screen reader announced a menu the keyboard could not drive. Two plain buttons
          after the trigger, reached with Tab, are what is actually here. */}
      <button
        ref={trigger}
        type="button"
        className="avatar"
        aria-label={person ? `Account: ${person.email}` : 'Account'}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <span aria-hidden="true">{initialsOf(person)}</span>
      </button>

      {open ? (
        <div className="account-menu" id={panelId}>
          {/* The address, because a person with two accounts in one organisation needs to know
              which one this is before deciding to leave it. Exactly the situation that made this
              button necessary. */}
          {/* Never "Signed in" without an address: with no answer from the server that claim is
              unconfirmed. Sign out below still works either way. */}
          <p className="account-menu-who">
            {person?.email ?? (loading ? 'Checking your session…' : 'Your session could not be confirmed')}
          </p>

          {others !== null && others > 0 ? (
            <button
              type="button"
              className="account-menu-item"
              disabled={leaving}
              onClick={() => void switchWorkspace()}
              title="Signs you out; after signing in you choose which workspace to open"
            >
              Switch workspace…
            </button>
          ) : null}

          <button
            type="button"
            className="account-menu-item"
            disabled={leaving}
            onClick={() => void leave()}
          >
            {leaving ? 'Signing out…' : 'Sign out'}
          </button>

          {leaveFailed ? (
            <p className="account-menu-error" role="alert">
              Sign-out did not reach Defenssive, so you may still be signed in. Try again.
            </p>
          ) : null}
        </div>
      ) : null}
      </div>
    </header>
  )
}

/**
 * One or two letters for the signed-in person.
 *
 * Falls back to a neutral glyph rather than to invented initials: there is no correct guess for
 * somebody whose name the server has not sent yet, and a wrong one is a different person's.
 */
function initialsOf(person: Me | null): string {
  if (!person) return '·'

  const source = person.name?.trim() || person.email
  const words = source.split(/[\s@._-]+/).filter(Boolean)

  if (words.length === 0) return '·'
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase()

  return (words[0]![0]! + words[1]![0]!).toUpperCase()
}
